import type { FC } from 'react'

import { useState } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { PageLink } from '@/components/kit/page-link'
import { Photo } from '@/components/kit/photo'
import { Reveal } from '@/components/kit/reveal'
import { Spark } from '@/components/kit/spark'
import { team } from '@/constants/about'

type Member = typeof team[number]

const TeamCard: FC<{ member: Member }> = ({ member }) => {
    const [flipped, setFlipped] = useState(false)

    return (
        <div className='group'>
            <div className='relative [perspective:1400px]'>
                <div
                    className={cn(
                        'relative aspect-[3/4] transition-transform duration-[900ms] ease-expo [transform-style:preserve-3d] group-focus-within:[transform:rotateY(180deg)] group-hover:[transform:rotateY(180deg)]',
                        flipped && '[transform:rotateY(180deg)]'
                    )}
                >
                    <div className='absolute inset-0 overflow-hidden rounded-[1.75rem] [backface-visibility:hidden]'>
                        <Photo
                            src={member.image}
                            alt={`Portrait of ${member.name}`}
                            sizes='(min-width: 1024px) 30vw, (min-width: 640px) 45vw, 90vw'
                            position={member.position}
                            grain
                            className='absolute inset-0'
                        />
                        <span
                            aria-hidden='true'
                            className='absolute bottom-4 left-4 rounded-full bg-cream/90 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-ink backdrop-blur'
                        >
                            Tap for the off-record bit
                        </span>
                    </div>

                    <div className='absolute inset-0 flex flex-col justify-between rounded-[1.75rem] bg-ink p-6 text-cream [backface-visibility:hidden] [transform:rotateY(180deg)]'>
                        <Spark className='size-7 text-ember' />
                        <p>
                            <span className='block font-mono text-[11px] uppercase tracking-[0.16em] text-cream/55'>Off the record</span>
                            <span className='mt-3 block font-heading text-[1.55rem] font-bold leading-[1.12] tracking-[-0.03em]'>
                                {member.fact}
                            </span>
                        </p>
                        <p className='text-sm text-cream/70'>{member.role}</p>
                    </div>
                </div>

                <button
                    type='button'
                    aria-pressed={flipped}
                    onClick={() => setFlipped(value => !value)}
                    className='absolute inset-0 rounded-[1.75rem] focus-visible:outline-offset-4'
                >
                    <span className='sr-only'>Show a fun fact about {member.name}</span>
                </button>
            </div>

            <h3 className='mt-5 text-2xl font-bold tracking-[-0.035em]'>
                {member.name}
            </h3>
            <p className='mt-1 text-[15px] text-stone'>
                {member.role}
            </p>
        </div>
    )
}

export const TeamGrid: FC = () => {
    return (
        <ul className='grid gap-x-5 gap-y-12 sm:grid-cols-2 lg:grid-cols-3'>
            {team.map((member, index) => (
                <Reveal
                    key={member.name}
                    as='li'
                    delay={(index % 3) * 100}
                >
                    <TeamCard member={member} />
                </Reveal>
            ))}

            <Reveal
                as='li'
                delay={200}
            >
                <PageLink
                    href='/contact'
                    data-cursor='Open'
                    className='group flex aspect-[3/4] flex-col justify-between rounded-[1.75rem] border-2 border-dashed border-ink/20 p-7 transition-[background-color,border-color] duration-500 hover:border-ember hover:bg-ember'
                >
                    <span className='font-mono text-[11px] uppercase tracking-[0.16em] text-stone transition-colors group-hover:text-ink'>
                        The empty chair
                    </span>
                    <span>
                        <span className='block font-heading text-[clamp(3rem,6vw,4.5rem)] font-bold leading-[0.88] tracking-[-0.04em]'>
                            You?
                        </span>
                        <span className='mt-4 block max-w-xs text-[15px] leading-relaxed text-stone transition-colors group-hover:text-ink'>
                            We are always keen to meet editors, forecasters and producers who think in first frames. Say hello.
                        </span>
                    </span>
                    <span className='flex size-12 items-center justify-center rounded-full bg-ink text-cream transition-transform duration-500 ease-expo group-hover:rotate-45'>
                        <ArrowUpRight className='size-5' />
                    </span>
                </PageLink>
            </Reveal>
        </ul>
    )
}
